import React, {
  ChangeEvent,
  FC,
  FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";

import Alert from "../Alert";
import Button from "../Button";
import ButtonPrimary from "../ButtonPrimary";
import Modal from "../Modal";
import { IEditProfileProps } from "./types";
import { ModalHeader } from "../Modal/ModalHeader";
import { ModalContent } from "../Modal/ModalContent";
import { ModalFooter } from "../Modal/ModalFooter";
import FormGroup from "../FormGroup";
import Label from "../Label";
import Input from "../Input";
import PasswordInput from "../PasswordInput";
import { getEmailLabel, getUsernameLabel } from "../../utils/common";
import { ACTION, SUBJECT } from "../../constants/permissions";
import Can from "../Can";

import "./styles.scss";

export const EditProfileModal: FC<IEditProfileProps> = ({
  children,
  className,
  isOpen,
  onDismiss,
  onSubmit,
  user,
  businessName,
  isPasswordDisabled,
  isUserNameDisabled,
  isSyncEmail = false,
  errors,
  isLoading,
  ...props
}) => {
  const [name, setName] = useState(user.name);
  const [email, setEmail] = useState(user.email);
  const [userName, setUserName] = useState(user.username || "");
  const [businessUserName, setBusinessUserName] = useState(businessName || "");
  const [password, setPassword] = useState("");
  const [isNameValid, setIsNameValid] = useState(true);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [isBusinessNameValid, setIsBusinessNameValid] = useState(true);
  const { t } = useTranslation();

  const emailLabel = useMemo(() => {
    return getEmailLabel(user.tenant_code);
  }, [user]);
  const usernameLabel = useMemo(() => {
    return getUsernameLabel(user.tenant_code);
  }, [user]);
  const handleChangeName = (e: ChangeEvent<HTMLInputElement>) => {
    setIsNameValid(true);
    setName(e.target.value);
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    setIsEmailValid(true);
    setEmail(e.target.value);
  };

  const handleChangePassword = (e: ChangeEvent<HTMLInputElement>) => {
    setIsPasswordValid(true);
    setPassword(e.target.value);
  };

  const handleUserNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (isSyncEmail) {
      handleChangeEmail(e);
    }

    setUserName(e.target.value);
  };

  const handleBusinessNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setIsBusinessNameValid(true);
    setBusinessUserName(e.target.value);
  };

  useEffect(() => {
    if (errors) {
      if (errors.name) {
        setName("");
        setIsNameValid(false);
      }

      if (errors.email) {
        setEmail("");
        setIsEmailValid(false);
      }

      if (errors.password) {
        setPassword("");
        setIsPasswordValid(false);
      }

      if (errors.businessName) {
        setBusinessUserName("");
        setIsBusinessNameValid(false);
      }
    }
  }, [errors]);

  const passwordValid = useMemo(() => {
    return isPasswordDisabled || password;
  }, [isPasswordDisabled, password]);

  const isFormValid = useMemo(() => {
    return name && email && passwordValid && userName && isBusinessNameValid;
  }, [name, email, passwordValid, userName, isBusinessNameValid]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    onSubmit(
      {
        name,
        email,
        currentPassword: password,
        username: userName,
      },
      businessUserName,
    );
  };
  const errorList = errors ? Object.values(errors) : [];

  return (
    <Modal
      className={classNames("ams-edit-profile-modal", className)}
      isOpen={isOpen}
      {...props}
    >
      <ModalHeader>{t("editProfileInformation")}</ModalHeader>
      <ModalContent>
        {!!errorList.length && (
          <div className="ams-edit-profile-modal__validation-alert">
            <Alert
              className="ams-edit-profile-modal__error-alert"
              variant="error"
              content={
                <ul className="ams-edit-profile-modal__error-list">
                  {errorList.map((errorData) => {
                    return <li key={errorData}>{t(`error.${errorData}`)}</li>;
                  })}
                </ul>
              }
            />
          </div>
        )}
        <form className="edit-profile-form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t("name")}</Label>
            <Input
              type="text"
              value={name}
              error={!isNameValid}
              onChange={handleChangeName}
              required
              id="name-input"
            />
          </FormGroup>

          {businessName && (
            <Can I={ACTION.WRITE} a={SUBJECT.OWNER}>
              <FormGroup>
                <Label>{t("businessName")}</Label>
                <Input
                  value={businessUserName}
                  error={!isBusinessNameValid}
                  onChange={handleBusinessNameChange}
                  type="text"
                  id="businessname-input"
                />
              </FormGroup>
            </Can>
          )}

          <FormGroup>
            <Label>{t(usernameLabel)}</Label>
            <Input
              disabled={isUserNameDisabled}
              value={userName}
              error={!isEmailValid}
              onChange={handleUserNameChange}
              type="text"
              id="username-input"
            />
          </FormGroup>

          {!isSyncEmail ? (
            <FormGroup>
              <Label>{t(emailLabel)}</Label>
              <Input
                type="email"
                value={email}
                error={!isEmailValid}
                onChange={handleChangeEmail}
                required
                id="email-input"
              />
            </FormGroup>
          ) : null}

          <FormGroup>
            <Label>{t("confirmPassword")}</Label>
            <PasswordInput
              value={password}
              error={!isPasswordValid}
              disabled={isPasswordDisabled}
              onChange={handleChangePassword}
              id="password-input"
            />
          </FormGroup>
        </form>
      </ModalContent>
      <ModalFooter>
        <Button onClick={onDismiss} className="ams-modal__action">
          {t("cancel")}
        </Button>
        <ButtonPrimary
          onClick={handleSubmit}
          className="ams-modal__action"
          disabled={!isFormValid}
          isLoading={isLoading}
        >
          {t("saveChanges")}
        </ButtonPrimary>
      </ModalFooter>
    </Modal>
  );
};
